import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faArrowLeft, faEye, faPencil } from '@fortawesome/free-solid-svg-icons';

import { Table } from '../../../shared/table/table';
import { TableAction, TableColumn } from '../../../shared/models/table-models';
import { Popup } from '../../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';
import { ToastService } from '../../../../core/shared/services/toast.service';
import { Setup } from '../../shared/service/setup';
import { CurrencyRatePayload } from '../../shared/models/guarantor.model';

const STATUS_ACTIVE = 706;
const STATUS_INACTIVE = 707;

interface CurrencyRateRow {
  srNo: number;
  crncyRtSeq: number | null;
  crncySeq: number | null;
  rt: number | null;
  stsKey: number;
  /** Rate shown with 2 decimals; the table's currency type would round them away. */
  rtDisplay: string;
  date: string | null;
}

/** Rates of one currency, opened from the Currency Name link on the currency list. */
@Component({
  selector: 'app-currency-rate',
  imports: [TranslatePipe, Table, Popup, FontAwesomeModule],
  templateUrl: './currency-rate.html',
  styleUrl: './currency-rate.css',
})
export class CurrencyRate implements OnInit {
  showPopup = false;
  faArrowLeft = faArrowLeft;
  faPencil = faPencil;
  faEye = faEye;
  popupTitle = '';
  isViewMode = false;

  crncySeq = 0;
  crncyNm = '';

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private setupService: Setup,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.crncySeq = Number(params['crncySeq'] ?? 0);
      this.crncyNm = params['crncyNm'] ?? '';
      this.loadRates();
    });
  }

  popupFields: DynamicFieldConfig[] = [];

  // ================= TABLE CONFIG =================
  columns: TableColumn[] = [
    { key: 'srNo', label: 'SETUP.CURRENCY_RATE.SR_NO', type: 'text', width: '16%' },
    { key: 'date', label: 'SETUP.CURRENCY_RATE.DATE', type: 'date', width: '24%' },
    { key: 'rtDisplay', label: 'SETUP.CURRENCY_RATE.RATE', type: 'text', width: '24%' },
    {
      key: 'stsKey',
      label: 'COMMON.STATUS',
      type: 'badge',
      width: '20%',
      map: { [STATUS_ACTIVE]: 'COMMON.ACTIVE', [STATUS_INACTIVE]: 'COMMON.INACTIVE' },
      badgeMap: { [STATUS_ACTIVE]: 'status-active', [STATUS_INACTIVE]: 'status-inactive' }
    }
  ];

  rates: CurrencyRateRow[] = [];
  loading = false;

  loadRates() {
    this.loading = true;
    this.setupService.getCurrencyRateList().subscribe({
      next: (res: any) => {
        this.loading = false;
        const list: any[] = Array.isArray(res) ? res : res?.data || [];

        this.rates = list
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          // The list covers every currency; keep only the one opened.
          .filter((item) => !this.crncySeq || Number(item?.crncySeq) === this.crncySeq)
          .map((item, index) => {
            const rt = item.rt === null || item.rt === undefined || item.rt === '' ? null : Number(item.rt);

            return {
              srNo: index + 1,
              crncyRtSeq: item.crncyRtSeq ?? null,
              crncySeq: item.crncySeq ?? null,
              rt,
              stsKey: Number(item.stsKey ?? STATUS_ACTIVE),
              rtDisplay: rt === null
                ? '-'
                : rt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
              // The list's date field name isn't fixed yet; take whichever the API sends.
              date: item.rtDt ?? item.crtdDt ?? item.lastUpdDt ?? null
            };
          });
      },
      error: (err) => {
        this.loading = false;
        console.error('Failed to load currency rates', err);
      }
    });
  }

  selectedRow: CurrencyRateRow | null = null;

  private buildFields(row: CurrencyRateRow | null, required: boolean): DynamicFieldConfig[] {
    return [
      {
        label: 'SETUP.CURRENCY_RATE.RATE',
        key: 'rt',
        type: 'text',
        value: row?.rt ?? '',
        inputmode: 'decimal',
        validations: required ? { required: true, nonNegativeDecimal: true, decimalPlaces: 2 } : undefined
      }
    ];
  }

  openCurrencyRate() {
    this.selectedRow = null;
    this.popupTitle = 'SETUP.CURRENCY_RATE.ADD_CURRENCY_RATE';
    this.isViewMode = false;
    this.popupFields = this.buildFields(null, true);
    this.showPopup = true;
  }

  // ================= TABLE ACTIONS =================
  actions: TableAction[] = [
    {
      icon: this.faPencil,
      class: 'text-indigo-600',
      tooltip: 'COMMON.EDIT',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.CURRENCY_RATE.EDIT_CURRENCY_RATE';
        this.isViewMode = false;
        this.popupFields = this.buildFields(row, true);
        this.showPopup = true;
      }
    },
    {
      icon: this.faEye,
      class: 'text-green-600',
      tooltip: 'COMMON.VIEW',
      onClick: (row: any) => {
        this.selectedRow = row;
        this.popupTitle = 'SETUP.CURRENCY_RATE.VIEW_CURRENCY_RATE';
        this.isViewMode = true;
        this.popupFields = this.buildFields(row, false);
        this.showPopup = true;
      }
    }
  ];

  /** Returns false so the popup only closes once the API succeeds. */
  SaveCurrencyRate(formData: any): boolean {
    if (!this.selectedRow && !this.crncySeq) {
      this.toast.error('SETUP.CURRENCY_RATE.CURRENCY_MISSING');
      return false;
    }

    const payload = this.createPayload(formData);

    this.setupService.saveCurrencyRate(payload).subscribe({
      next: () => {
        this.toast.success('SETUP.SAVED_SUCCESSFULLY');
        this.showPopup = false;
        this.loadRates();
      },
      error: (err) => {
        console.error('Save failed', err);
        this.toast.error(err?.error?.message || 'SETUP.FAILED_TO_SAVE_RECORD');
      }
    });

    return false;
  }

  private createPayload(formData: any): CurrencyRatePayload {
    const row = this.selectedRow;

    return {
      crncyRtSeq: row?.crncyRtSeq ?? null,
      crncySeq: row?.crncySeq ?? this.crncySeq,
      rt: Number(formData.rt),
      delFlg: 0
    };
  }
}
